import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { useAsyncCallback } from '../../../hooks'
import { stopPropagation } from '../../../utils/events'
import { NotificationInlineList } from '../../Notification'
import { ProfileAISummaryWidgetContent } from '../ProfileAISummaryWidgetContent'
import { ProfileAISummaryWidgetFooter } from '../ProfileAISummaryWidgetFooter'
import { ProfileAISummaryWidgetHeaderActions } from '../ProfileAISummaryWidgetHeaderActions'
import { ProfileAISummaryWidgetHeaderContentTitle } from '../ProfileAISummaryWidgetHeaderContentTitle'
import { ProfileAISummaryWidgetLoadButton } from '../ProfileAISummaryWidgetLoadButton'
import { Widget } from '../Widget'
import { useWidgetAISummaryLoader } from '../Widget.hooks'

import { ProfileAISummaryWidgetStateKey } from './ProfileAISummaryWidget.constants'
import { getNotifications } from './ProfileAISummaryWidget.utils'

import * as styles from './ProfileAISummaryWidget.module.css'

function ProfileAISummaryWidget({
  className = styles.ProfileAISummaryWidget,
  contentContainerProps,
  data,
  generateSummaryParams,
  initiallyExpanded = true,
  isArchived,
  isSkeleton,
  loadButtonProps: {
    textKey:
      loadButtonTextKey = 'Common.ProfileWidgetAISummaryLoadButtonTextKey',
    tooltipKey:
      loadButtonTooltipKey = 'Common.ProfileWidgetAISummaryLoadButtonTooltipKey',
    ...loadButtonProps
  } = EMPTY_OBJECT,
  textProps,
  titleKey = 'Common.ProfileWidgetAISummaryTitleKey',
  onError,
  onGenerateSummary,
  onGetTaskStatus,
  onReloadData,
  onSuccess,
  ...restProps
}) {
  const { t } = useTranslation()

  const contentRef = useRef(null)

  const [hasUserGenerated, setHasUserGenerated] = useState(false)

  const { handleLoad, loading } = useWidgetAISummaryLoader({
    onError,
    onGenerateSummary,
    onGetTaskStatus,
    onReloadData,
    onSuccess,
  })

  const {
    can_generate: canGenerate,
    files,
    text,
    warnings = EMPTY_LIST,
  } = data ?? EMPTY_OBJECT

  const handleClickGenerateSummary = useAsyncCallback(
    event => {
      stopPropagation(event)

      setHasUserGenerated(true)

      return handleLoad(generateSummaryParams)
    },
    [generateSummaryParams, handleLoad]
  )

  const notifications = getNotifications({
    canGenerate,
    isArchived,
    loading,
    t,
    text,
    warnings,
    onGenerateSummary: handleClickGenerateSummary,
  })

  const shouldRenderWidget = isSkeleton || data != null

  const canInitialGenerate = canGenerate && !text

  // A pre-existing summary loads collapsed; one the user just generated stays open — it keeps
  // the generate-state instance, so an earlier "collapse all" does not apply to it.
  const shouldCollapseExisting = !initiallyExpanded
  const widgetInitiallyExpanded =
    isSkeleton || canInitialGenerate || !shouldCollapseExisting
      ? true
      : hasUserGenerated
  const widgetStateKey = isSkeleton
    ? ProfileAISummaryWidgetStateKey.SKELETON
    : canInitialGenerate || hasUserGenerated
      ? ProfileAISummaryWidgetStateKey.GENERATE
      : ProfileAISummaryWidgetStateKey.CONTENT

  return shouldRenderWidget ? (
    <Widget
      {...restProps}
      Header={canInitialGenerate ? null : undefined}
      className={className}
      contentContainerProps={contentContainerProps}
      headerProps={{
        contentProps: {
          Actions: ProfileAISummaryWidgetHeaderActions,
          Title: ProfileAISummaryWidgetHeaderContentTitle,
        },
      }}
      initiallyExpanded={widgetInitiallyExpanded}
      isSkeleton={isSkeleton}
      key={widgetStateKey}
      title={t(titleKey)}
    >
      {canInitialGenerate ? (
        <ProfileAISummaryWidgetLoadButton
          {...loadButtonProps}
          loading={loading}
          text={t(loadButtonTextKey)}
          tooltip={t(loadButtonTooltipKey)}
          onClick={loading ? null : handleClickGenerateSummary}
        />
      ) : (
        <div className={styles.contentContainer}>
          {notifications.length > 0 ? (
            <NotificationInlineList items={notifications} />
          ) : null}
          {text ? (
            <ProfileAISummaryWidgetContent
              {...textProps}
              files={files}
              ref={contentRef}
              text={text}
            />
          ) : null}
          {text ? (
            <ProfileAISummaryWidgetFooter
              canRefresh={canGenerate}
              contentRef={contentRef}
              loading={loading}
              onRefresh={loading ? null : handleClickGenerateSummary}
            />
          ) : null}
        </div>
      )}
    </Widget>
  ) : null
}

export default ProfileAISummaryWidget
